import { Dispatch, SetStateAction, useRef, useState } from 'react';

import { FULL_PERCENT, PROGRESS_INTERVAL } from '@hooks/constants';
import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setChartsReceivedAt } from '@slices/kpi';

import { getKpiCharts, getProgress, runProcessKpi, startProgress } from '@api/kpi';

import { handleErrorFromMessage } from '@utils/handleRequestError';
import { getDateWithoutOffset } from '@utils/kpi-utils/date';

import { IResponseError } from '@interfaces/errors/apiErrors';
import { IKpiChartsDTO, IProgressKpiDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IChart } from '@interfaces/kpi/kpiCommon';

import { EUserRoles } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import {
	KPI_CHARTS_DRILL_DOWN,
	KPI_PROGRESS_DRILL_DOWN,
	KPI_START_PROGRESS_DRILL_DOWN,
} from '@constants/queryKeys';

export const useMutateKpiChartsDrillDown = (setErrorText: Dispatch<SetStateAction<string | undefined>>) => {
	const dispatch = useAppDispatch();

	const [refetchIntervalProgress, setRefetchIntervalProgress]
		= useState<number | false>(PROGRESS_INTERVAL);
	const [chartData, setChartData] = useState<IKpiChartsDTO | undefined>();

	const onFinalSuccessRef = useRef<(() => void) | undefined>();

	const resetIntervals = () => setRefetchIntervalProgress(PROGRESS_INTERVAL);

	const {
		data: progressId,
		mutate: runProgress,
		isLoading: isLoadingProgress,
	} = useMutation<IKpiChartsDTO>({
		mutationKey: [KPI_START_PROGRESS_DRILL_DOWN],
		mutationFn: startProgress,
		allowedRoles: [
			EUserRoles.networkOperatorKpiUpdater,
			EUserRoles.networkOperatorKpiViewer,
		],
		onSuccess: async (progressId: string, variables) => {
			await setChartData(variables);
			await runProcessKpi({ ...variables, progressId });
			await resetIntervals();
		},
	});

	const {
		data: progress,
		isLoading: isLoadingProgressData,
		isFetching: isFetchingProgressData,
	} = useQuery<IProgressKpiDTO>({
		queryKey: [KPI_PROGRESS_DRILL_DOWN, progressId],
		queryFn: () => getProgress(progressId!),
		refetchInterval: refetchIntervalProgress,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		onSuccess: (data: IProgressKpiDTO) => {
			if (data.percentsProgress !== null
				&& Math.floor(data.percentsProgress) === FULL_PERCENT
				&& !data.isDataLoadingInProgress) {
				const isFullProgress = progressId === data?.id && data.percentsProgress === FULL_PERCENT;

				if (isFullProgress && chartData) {
					getDrillDownCharts({ ...chartData, progressId });
					setRefetchIntervalProgress(false);
				}

			}
		},
		enabled: [!!progressId],
		allowedRoles: [
			EUserRoles.networkOperatorKpiUpdater,
			EUserRoles.networkOperatorKpiViewer,
		],
		onError: (err: IResponseError) => {
			setRefetchIntervalProgress(false);
			handleErrorFromMessage(err);
		},
	});

	const {
		data: drillDownDataCharts,
		mutate: getDrillDownCharts,
		isLoading: isLoadingCharts,
	} = useMutation<IKpiChartsDTO>({
		mutationKey: [KPI_CHARTS_DRILL_DOWN],
		mutationFn: (data: IKpiChartsDTO) => {
			setErrorText(undefined);

			return getKpiCharts({ ...data });
		},
		select: (charts: Array<IChart>) => {
			return charts.map(chart => ({ ...chart, isOpen: true, isThreshold: false }));
		},
		refetchOnWindowFocus: false,
		allowedRoles: [
			EUserRoles.networkOperatorKpiUpdater,
			EUserRoles.networkOperatorKpiViewer,
		],
		onSuccess: () => {
			if (onFinalSuccessRef.current) {
				onFinalSuccessRef.current();
				onFinalSuccessRef.current = undefined;
			}

			dispatch(setChartsReceivedAt(getDateWithoutOffset(new Date().getTime()).getTime()));
		},
		onError: (err: IResponseError) => {
			onFinalSuccessRef.current = undefined;

			if (err.errors[0].code === EErrorCodes.couldNotApplyKpiToSites) {
				setErrorText(err.errors[0].message);
			} else if (err.errors[0].code === EErrorCodes.notFoundAggregationType) {
				setErrorText('No KPI charts available');
			} else {
				setErrorText(undefined);

				handleErrorFromMessage(err);
			}
		},
	});

	const runDrillDownProgress = (
		data: IKpiChartsDTO,
		onSuccess?: () => void,
	) => {
		onFinalSuccessRef.current = onSuccess;
		runProgress(data);
	};

	return {
		drillDownDataCharts,
		isLoadingDrillDownCharts: (isLoadingProgressData && !!progress) ||
			(!!progress && progress?.percentsProgress !== FULL_PERCENT) ||
			isLoadingCharts || isLoadingProgress || isFetchingProgressData,
		runDrillDownProgress,
		progress,
	};
};
